import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Button,
  Stack,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { get, post } from '../../../axios/apiService';
import DroppableList from 'ui-component/Drag/DroppableList';
import DraggableItem from 'ui-component/Drag/DraggableItem';
import toast from 'react-hot-toast';

const PlanAssignmentsDialog = ({ open, plan, onClose, onAssignmentsUpdated, canEdit = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [assignedFeatures, setAssignedFeatures] = useState([]);
  const [availableCalculators, setAvailableCalculators] = useState([]);
  const [assignedCalculators, setAssignedCalculators] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !plan) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [allFeaturesRaw, allCalculatorsRaw] = await Promise.all([
          get("Feature/all", null),
          get("Calculators", null)
        ]);

        // ---------------- FEATURES ----------------
        const allFeatures = allFeaturesRaw.map((f) => ({
          id: f.id,
          name: f.name
        }));

        // ---------------- CALCULATORS (MAP FIRST) ----------------
        let allCalculators = allCalculatorsRaw.values.map((c) => ({
          id: c.id,
          name: c.calculatorName,
          parentId: c.parentId,
          isAccessible: c.isAccessible
        }));

        // ---------------- ASSIGNED ----------------
        const assignedRaw = plan.assignments || {};

        const assignedFeaturesList =
          assignedRaw.features?.map((f) => ({
            id: f.featureId,
            name: f.featureName
          })) || [];

        // Calculators returned from backend as assigned
        let assignedCalculatorsList =
          (assignedRaw.calculators || []).map((c) => ({
            id: c.calculatorId,
            name: c.calculatorName,
            parentId: allCalculators.find(x => x.id === c.calculatorId)?.parentId
          }));

        // --- NEW: force all parentId === "1" into assigned ---
        const parentCalcs = allCalculators.filter(c => c.parentId === "1");

        // merge into assigned list (avoid duplicates)
        parentCalcs.forEach(pc => {
          if (!assignedCalculatorsList.some(a => a.id === pc.id)) {
            assignedCalculatorsList.push({
              id: pc.id,
              name: pc.name,
              parentId: pc.parentId
            });
          }
        });


        // ---------------- ID LISTS ----------------
        const assignedFeatureIds = assignedFeaturesList.map((f) => f.id);
        const assignedCalcIds = assignedCalculatorsList.map((c) => c.id);

        // ---------------- SET STATE ----------------
        setAssignedFeatures(assignedFeaturesList);
        setAssignedCalculators(assignedCalculatorsList);

        setAvailableFeatures(
          allFeatures.filter((f) => !assignedFeatureIds.includes(f.id))
        );

        setAvailableCalculators(
          allCalculators.filter(c =>
            !assignedCalcIds.includes(c.id) && c.parentId !== "1"
          )
        );


      } catch (e) {
        console.error("Failed to load assignment data:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, plan]);



  const handleDragStart = (event) => {
    if (!canEdit) return;
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    if (!canEdit) return;
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const moveItem = (source, target, setSource, setTarget, activeId) => {
      const moved = source.find((x) => x.id === activeId);
      if (!moved) return;
      setSource((prev) => prev.filter((x) => x.id !== activeId));
      setTarget((prev) => [...prev, moved]);
    };

    if (tabValue === 0) {
      if (over.id === 'assigned')
        moveItem(availableFeatures, assignedFeatures, setAvailableFeatures, setAssignedFeatures, active.id);
      else if (over.id === 'available')
        moveItem(assignedFeatures, availableFeatures, setAssignedFeatures, setAvailableFeatures, active.id);
    } else {
      const activeCalc = [...availableCalculators, ...assignedCalculators]
        .find(x => x.id === active.id);

      // 🚫 BLOCK movement if calculator is parentId === "1"
      if (activeCalc?.parentId === "1") {
        // Do not move it anywhere
        return;
      }

      if (over.id === 'assigned')
        moveItem(availableCalculators, assignedCalculators, setAvailableCalculators, setAssignedCalculators, active.id);
      else if (over.id === 'available')
        moveItem(assignedCalculators, availableCalculators, setAssignedCalculators, setAvailableCalculators, active.id);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const body = {
      planId: plan.id,
      featureIds: assignedFeatures.map((f) => f.id),
      calculatorIds: assignedCalculators.map((c) => c.id)
    };

    try {
      const response = await post(`Plans/${plan.id}/assignments`, body, 'POST');
      console.log(response?.status, response.message)
      if (response.message) {
        toast.success(response.message)

        const updatedPlan = {
          ...plan,
          featureAssignmentDto: assignedFeatures.map(f => ({
            featureId: f.id,
            featureName: f.name
          })),
          calculatorAssignmentDto: assignedCalculators.map(c => ({
            calculatorId: c.id,
            calculatorName: c.name
          }))
        };
        onAssignmentsUpdated(updatedPlan);
        onClose();
      } else {
        toast.error("Something went wrong while updating assignments.");
      }
    } catch (err) {
      console.error('Save failed', err);
      toast.error("Failed to save assignments.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          p: 1.5
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 20 }}>
        {plan?.name} plan
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Features" />
          <Tab label="Calculators" />
        </Tabs>

        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DroppableList
                id="available"
                items={tabValue === 0 ? availableFeatures : availableCalculators}
                title={tabValue === 0 ? 'Available Features' : 'Available Calculators'}
                isLoading={loading}
                canEdit={canEdit}
              />
            </Grid>
            <Grid item xs={6}>
              <DroppableList
                id="assigned"
                items={tabValue === 0 ? assignedFeatures : assignedCalculators}
                title={tabValue === 0 ? 'Assigned Features' : 'Assigned Calculators'}
                isLoading={loading}
                canEdit={canEdit}
              />
            </Grid>
          </Grid>
          <DragOverlay>
            {activeId ? (() => {
              const allItems = tabValue === 0
                ? [...availableFeatures, ...assignedFeatures]
                : [...availableCalculators, ...assignedCalculators];
              const activeItem = allItems.find(item => item.id === activeId);
              if (!activeItem) return null;
              const isAssigned = tabValue === 0
                ? assignedFeatures.some(f => f.id === activeId)
                : assignedCalculators.some(c => c.id === activeId);
              const indicatorColor = isAssigned ? "#3CB371" : "#1976d2";
              return (
                <DraggableItem id={activeId} name={activeItem.name} indicatorColor={indicatorColor} isHidden={activeItem.parentId == "1"} canEdit={canEdit} />
              );
            })() : null}
          </DragOverlay>
        </DndContext>

        <Stack direction="row" justifyContent="flex-end" mt={3} gap={1.5}>
          <Button onClick={onClose}>Cancel</Button>
          {
            canEdit && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canEdit}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )
          }
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PlanAssignmentsDialog;
